import { vec3 } from "gl-matrix";
import { Ray } from "../Math/Ray";
import { BoundingBox, IBoundingBoxObject, lerp } from '@spi/Common/Util';

export class PathNode implements IBoundingBoxObject {

    private bounds: BoundingBox;
    public id: number;
    public neighbours: PathNode[];
    public heightGrid: Float32Array[];
    public reference: string;

    constructor(public min: vec3 | number[], public max: vec3 | number[]) {
        this.bounds = new BoundingBox(min, max);
        this.heightGrid = [];
        this.neighbours = [];
        this.reference = null;
    }

    public addNeighbour(neighbour: PathNode) {
        // TODO: refactor array to Set?

        if (this.neighbours.indexOf(neighbour) == -1) {
            this.neighbours.push(neighbour);
        }
        if (neighbour.neighbours.indexOf(this) == -1) {
            neighbour.neighbours.push(this);
        }
    }

    public get position(): vec3 {
        return this.bounds.getCenter();
    }

    public get gridWidth(): number {
        if (this.heightGrid.length == 0) {
            return 0;
        } else {
            return this.heightGrid[0].length;
        }
    }

    public get gridHeight(): number {
        return this.heightGrid.length;
    }

    getPosition(): vec3 {
        return this.position;
    }

    getBoundingBox(): BoundingBox {
        return this.bounds;
    }
    canIntersectRay(): boolean {
        return false;
    }
    intersectsRay(ray: Ray): boolean {
        return false;
    }

    public reduceHeightGrid() {
        // TODO: reduce the number of elements in the height grid
        // - sample the grid and use values to create a new grid
    }

    public getHeight(position: vec3 | number[]): number {

        // for (let d = 0; d < 2; d++) {
        //     if (position[d] <= this.min[d] || position[d] >= this.max[d]) {
        //         console.warn(`Sampling height using out of bounds position`, position, this);
        //         return 0;
        //     }
        // }

        let relativePosition = vec3.sub(vec3.create(), this.max, position);
        let offset = vec3.sub(vec3.create(), this.max, this.min);
        let div = vec3.div(vec3.create(), relativePosition, offset);

        let x = Math.abs(div[0]);
        let y = Math.abs(div[1]);

        if (x < 0 || x > 1) {
            // console.log(`Invalid x ${x}, clamped to 0.0 - 1.0`);
            x = x > 1 ? 1 : 0;
        } else if (y < 0 || y > 1) {
            // console.log(`Invalid y ${y}, clamped to 0.0 - 1.0`);
            y = y > 1 ? 1 : 0;
        }

        x = this.gridWidth - 1 - Math.abs((this.gridWidth - 1) * x);
        y = this.gridHeight - 1 - Math.abs((this.gridHeight - 1) * y);

        return this.sampleHeight(Math.abs(x), Math.abs(y), position);
    }

    public sampleHeight(x: number, y: number, position: vec3 | number[]) {

        let xLow = Math.floor(x);
        let yLow = Math.floor(y);
        let xHigh = Math.ceil(x);
        let yHigh = Math.ceil(y);

        let distX = x - xLow;
        let distY = y - yLow;

        if (yLow >= this.gridHeight || yHigh >= this.gridHeight
            ||
            xLow >= this.gridWidth || xHigh >= this.gridWidth) {
            console.warn(`PathNode ${this.id}: Sampling height for ${x}, ${y}, but this exceeds the grid ${this.gridWidth}x${this.gridHeight}`, position);
            return 0;
        }

        try {
            return lerp(
                lerp(this.heightGrid[yLow][xLow], this.heightGrid[yLow][xHigh], distX),
                lerp(this.heightGrid[yHigh][xLow], this.heightGrid[yHigh][xHigh], distX),
                distY
            );
        } catch (e) {
            console.error(e);
        }
    }

    getRayIntersection(ray: Ray, testBorders: boolean): vec3 {
        // TBD: cache these faces?
        // - pro: less performance per click
        // - con: uses a bunch of extra memory per node

        let surface = testBorders ? [] : this.generateSurface(5, 10);
        let box = testBorders ? this.generateBox() : [];

        for (let face of [...surface, ...box]) {
            let intersection = ray.getPlaneIntersection(face.vertexA, face.vertexB, face.vertexC);
            if (intersection) {
                return intersection;
            }
        }

        return null;
    }

    private generateBox(): FaceLike[] {
        let bounds = this.getBoundingBox();
        let min = bounds.min;
        let max = bounds.max;

        let topLeftLow = [min[0], min[1], min[2]];
        let topRightLow = [max[0], min[1], min[2]];
        let bottomLeftLow = [min[0], max[1], min[2]];
        let bottomRightLow = [max[0], max[1], min[2]];

        let topLeftHigh = [min[0], min[1], max[2]];
        let topRightHigh = [max[0], min[1], max[2]];
        let bottomLeftHigh = [min[0], max[1], max[2]];
        let bottomRightHigh = [max[0], max[1], max[2]];

        let faceFloorA = {
            vertexA: topLeftLow,
            vertexB: topRightLow,
            vertexC: bottomLeftLow
        };

        let faceFloorB = {
            vertexA: topRightLow,
            vertexB: bottomLeftLow,
            vertexC: bottomRightLow
        };

        let northSideA = {
            vertexA: topLeftHigh,
            vertexB: topLeftLow,
            vertexC: topRightLow
        };

        let northSideB = {
            vertexA: topRightLow,
            vertexB: topLeftHigh,
            vertexC: topRightHigh
        };

        let southSideA = {
            vertexA: bottomLeftHigh,
            vertexB: bottomLeftLow,
            vertexC: bottomRightLow
        };

        let southSideB = {
            vertexA: bottomRightLow,
            vertexB: bottomLeftHigh,
            vertexC: bottomRightHigh
        };


        let westSideA = {
            vertexA: topLeftLow,
            vertexB: bottomLeftLow,
            vertexC: bottomLeftHigh
        };

        let westSideB = {
            vertexA: topLeftHigh,
            vertexB: bottomLeftHigh,
            vertexC: topLeftLow
        };

        let eastSideA = {
            vertexA: topRightLow,
            vertexB: bottomRightLow,
            vertexC: bottomRightHigh
        };

        let eastSideB = {
            vertexA: topRightHigh,
            vertexB: bottomRightHigh,
            vertexC: topRightLow
        };

        return [
            faceFloorA, faceFloorB,
            northSideA, northSideB,
            southSideA, southSideB,
            westSideA, westSideB,
            eastSideA, eastSideB
        ];
    }

    public generateSurface(minDivisionSize: number, maxDivisions: number): FaceLike[] {
        let bounds = this.getBoundingBox();
        let min = bounds.min;
        let max = bounds.max;

        let xd = max[0] - min[0];
        let yd = max[1] - min[1];

        let xDivs = Math.ceil(xd / minDivisionSize);
        if (xDivs > maxDivisions) {
            xDivs = maxDivisions;
        }

        let yDivs = Math.ceil(yd / minDivisionSize);
        if (yDivs > maxDivisions) {
            yDivs = maxDivisions;
        }

        let xStep = xd / xDivs;
        let yStep = yd / yDivs;


        let vertices: vec3[][] = [];

        // Generate the vertices
        for (let x = 0; x < xDivs + 1; x++) {
            vertices.push([]);
            for (let y = 0; y < yDivs + 1; y++) {

                let vx = x * xStep + min[0];
                let vy = y * yStep + min[1];

                let pos = vec3.fromValues(vx, vy, 0);
                pos[2] = this.getHeight(pos);

                vertices[x].push(pos);

            }
        }

        // Generate the faces (two triangles)
        let faces: FaceLike[] = [];
        for (let x = 0; x < xDivs; x++) {

            for (let y = 0; y < yDivs; y++) {
                let faceA = {
                    vertexA: vertices[x][y],
                    vertexB: vertices[x + 1][y],
                    vertexC: vertices[x][y + 1]
                };
                let faceB = {
                    vertexA: vertices[x + 1][y],
                    vertexB: vertices[x + 1][y + 1],
                    vertexC: vertices[x][y + 1]
                };

                faces.push(faceA, faceB);
            }
        }

        return faces;
    }
}


type FaceLike = {
    vertexA: vec3 | number[],
    vertexB: vec3 | number[],
    vertexC: vec3 | number[]
}